import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import TextField from "components/form/textField/TextField";
import { ButtonNew as Button } from "components/buttonNew";

import "./customerCreditTransactionNoteEditor.scss";

interface IProps {
  open: boolean;
  anchorRect: DOMRect | null;
  value: string;
  placeholder?: string;
  rows?: number;
  /** Preferred horizontal direction relative to the anchor. Defaults to "left". */
  openTo?: "left" | "right";
  saving?: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
}

const PANEL_WIDTH = 328;
const VIEWPORT_PADDING = 16;

export default function CustomerCreditTransactionNoteEditor(props: IProps) {
  const {
    open,
    anchorRect,
    value,
    placeholder,
    rows = 4,
    openTo = "left",
    saving = false,
    onClose,
    onSave,
  } = props;
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [draftValue, setDraftValue] = useState(value);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (open) {
      setDraftValue(value);
    }
  }, [open, value]);

  useLayoutEffect(() => {
    if (!open || !anchorRect) {
      return;
    }

    const panelHeight = panelRef.current?.offsetHeight ?? 224;
    let top = anchorRect.top;
    let left = openTo === "right" ? anchorRect.left : anchorRect.right - PANEL_WIDTH;

    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    if (left + PANEL_WIDTH > window.innerWidth - VIEWPORT_PADDING) {
      left = Math.max(VIEWPORT_PADDING, window.innerWidth - PANEL_WIDTH - VIEWPORT_PADDING);
    }

    if (top + panelHeight > window.innerHeight - VIEWPORT_PADDING) {
      top = Math.max(VIEWPORT_PADDING, window.innerHeight - panelHeight - VIEWPORT_PADDING);
    }

    setPosition({ top, left });
  }, [open, anchorRect, draftValue, openTo]);

  if (!open || !anchorRect) {
    return null;
  }

  return createPortal(
    <>
      <button type="button" className="customer-credit-note-editor__backdrop" onClick={onClose} aria-label={t("Close")} />
      <div
        ref={panelRef}
        className="customer-credit-note-editor"
        style={{ top: position.top, left: position.left, width: PANEL_WIDTH }}
      >
        <TextField
          id="credit-book-editor-field"
          labelHidden
          value={draftValue}
          placeholder={placeholder ?? t("Add a note for your records...")}
          rows={rows}
          noResize
          autoFocus
          onChange={event => setDraftValue(event.target.value)}
        />

        <div className="customer-credit-note-editor__footer">
          <Button type="default" size="medium" disabled={saving} onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button type="primary" size="medium" loading={saving} onClick={() => onSave(draftValue)}>
            {t("Update changes")}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}
